import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AppContext';
import { Film, Music, Play, Loader2, Share2 } from 'lucide-react';
import { shareVideo } from '../utils/share';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const FILM_GENRES = ['Drama', 'Documentary', 'Sci-Fi', 'Animation', 'Experimental', 'Short Film', 'Other'];

const FilmCard = ({ film }) => {
  const navigate = useNavigate();
  const thumb = film.thumbnail_path ? `${BACKEND}/api/files/${film.thumbnail_path}` : null;
  return (
    <div className="relative group">
    <button
      data-testid={`film-card-${film.id}`}
      onClick={() => navigate(`/films/${film.id}`)}
      className="text-left w-full"
    >
      <div className="relative aspect-video rounded-xl overflow-hidden bg-zinc-900 border border-zinc-800">
        {thumb ? (
          <img src={thumb} alt={film.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-700"><Film className="w-8 h-8" /></div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <Play className="w-10 h-10 text-white" fill="white" />
        </div>
        <span className="absolute top-2 left-2 text-[10px] px-1.5 py-0.5 rounded-full bg-black/70 text-white border border-white/20">Film</span>
      </div>
      <div className="mt-2">
        <p className="text-sm font-semibold text-white truncate">{film.title}</p>
        <p className="text-xs text-zinc-500 truncate">{film.artist} · {film.genre}</p>
      </div>
    </button>
      <button
        type="button"
        aria-label={`Share ${film.title}`}
        data-testid={`film-card-share-${film.id}`}
        onClick={() => shareVideo(film)}
        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/70 border border-white/20 text-white flex items-center justify-center hover:bg-white hover:text-black transition-colors"
      >
        <Share2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

const Rail = ({ title, films, testid }) => {
  if (!films || films.length === 0) return null;
  return (
    <section className="mb-8" data-testid={testid}>
      <h2 className="text-lg font-semibold text-white mb-3 px-1">{title}</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {films.map((f) => <FilmCard key={f.id} film={f} />)}
      </div>
    </section>
  );
};

export const FilmsPage = () => {
  const { axiosInstance } = useAuth();
  const [feed, setFeed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [genre, setGenre] = useState('');
  const [filtered, setFiltered] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/films/feed');
      setFeed(res.data);
    } catch (e) {
      setFeed({ trending: [], from_agents: [], from_humans: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!genre) { setFiltered(null); return; }
    axiosInstance.get(`/films?genre=${encodeURIComponent(genre)}`).then((r) => setFiltered(r.data)).catch(() => setFiltered([]));
  }, [genre, axiosInstance]);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-testid="films-page">
      {/* Music | Film toggle */}
      <div className="flex items-center gap-2 mb-5" data-testid="mode-toggle">
        <Link to="/" data-testid="toggle-music" className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors">
          <Music className="w-4 h-4" /> Music
        </Link>
        <span className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold bg-white text-black" data-testid="toggle-film-active">
          <Film className="w-4 h-4" /> Film
        </span>
      </div>

      {/* Genre filters */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-6" style={{ scrollbarWidth: 'none' }}>
        <button onClick={() => setGenre('')} data-testid="film-genre-all" className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium ${!genre ? 'bg-white text-black' : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'}`}>All</button>
        {FILM_GENRES.map((g) => (
          <button key={g} onClick={() => setGenre(g)} data-testid={`film-genre-${g.toLowerCase().replace(/\s/g, '-')}`} className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium ${genre === g ? 'bg-white text-black' : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'}`}>{g}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : filtered !== null ? (
        filtered.length === 0 ? (
          <div className="text-center py-16 text-zinc-500" data-testid="films-empty">No films in this genre yet.</div>
        ) : (
          <Rail title={`${genre} Films`} films={filtered} testid="film-rail-genre" />
        )
      ) : !feed || feed.total === 0 ? (
        <div className="text-center py-16 text-zinc-500" data-testid="films-empty">
          <Film className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>No films yet. Be the first to upload one from your Studio.</p>
        </div>
      ) : (
        <>
          {feed.featured && (
            <section className="mb-8" data-testid="film-featured">
              <h2 className="text-lg font-semibold text-white mb-3 px-1">Featured Film</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FilmCard film={feed.featured} />
              </div>
            </section>
          )}
          <Rail title="Trending Films" films={feed.trending} testid="film-rail-trending" />
          <Rail title="From AI Agents" films={feed.from_agents} testid="film-rail-agents" />
          {feed.new && <Rail title="New Films" films={feed.new} testid="film-rail-new" />}
        </>
      )}
    </div>
  );
};

export default FilmsPage;
